import { useVirtualizer, notUndefined } from '@tanstack/react-virtual'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { SortingState, ColumnDef } from '@tanstack/react-table'
import { cn } from 'components/UI/cn'
import { ArrowDownUp, SortAsc, SortDesc } from 'lucide-react'
import { CSVRow } from './importer-types'
import { useRef, useState } from 'react'

export function ImporterDataTable({
  data,
  columns,
}: {
  data: CSVRow[]
  columns: ColumnDef<CSVRow>[]
}) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [sorting, setSorting] = useState<SortingState>([])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  })

  const { rows } = table.getRowModel()

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    overscan: 20,
  })
  const items = virtualizer.getVirtualItems()
  const [before, after] =
    items.length > 0
      ? [
          notUndefined(items[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0]
  const colSpan = 8

  if (!data.length) {
    return <div>No data</div>
  }

  return (
    <div ref={parentRef} className="h-full overflow-auto border-1">
      <table className="border-collapse">
        <thead className="border-separate">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: header.getSize(),
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'rgb(252, 252, 253)',
                      borderBottom: '1px solid #E2E8F0',
                      height: '60px',
                      color: '#222222',
                      zIndex: 20,
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn('truncate h-full p-2 min-w-0')}
                        style={{
                          width: header.getSize(),
                        }}
                      >
                        <div
                          className={'flex flex-row items-start gap-2 h-full'}
                        >
                          {header.column.getCanSort() && (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              className={cn('cursor-pointer select-none', {
                                'text-blue-500': header.column.getIsSorted(),
                              })}
                            >
                              {{
                                asc: <SortAsc size={16} />,
                                desc: <SortDesc size={16} />,
                              }[header.column.getIsSorted() as string] ?? (
                                <ArrowDownUp size={16} />
                              )}
                            </div>
                          )}
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {before > 0 && (
            <tr>
              <td colSpan={colSpan} style={{ height: before }} />
            </tr>
          )}
          {items.map((virtualRow) => {
            const row = rows[virtualRow.index]
            return (
              <tr
                key={row.id}
                style={{
                  height: `${virtualRow.size}px`,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  return flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )
                })}
              </tr>
            )
          })}
          {after > 0 && (
            <tr>
              <td colSpan={colSpan} style={{ height: after }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
